import React, { useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '../store';
import {
    fetchAllStages,
    fetchActiveStages,
    fetchStagesWithCounts,
    createStage,
    updateStage,
    deleteStage,
    refreshCandidateCounts,
    clearError,
    handleCandidateStageChange,
    updateCandidateCount,
    incrementCandidateCount,
    decrementCandidateCount
} from '../slices/stagesSlice';
import {
    selectStages,
    selectActiveStages,
    selectActiveStagesSortedByOrder,
    selectStagesSortedByOrder,
    selectStagesLoading,
    selectStagesError,
    selectNewCandidateCount,
    selectCandidateCounts,
    selectTotalCandidateCount,
    selectStageById,
    selectStageStats,
    selectIsStagesDataStale,
    selectStagesWithCandidateCounts,
    selectActiveStagesWithCandidateCounts
} from '../selectors/stageSelectors';
import { StageCreateDto, StageUpdateDto } from '../../types/types';

/**
 * Custom hook for managing stage state and operations
 * Provides a convenient interface for components to interact with stage data
 */
export const useStages = () => {
    const dispatch = useAppDispatch();

    // Selectors
    const stages = useAppSelector(selectStages);
    const activeStages = useAppSelector(selectActiveStages);
    const activeStagesSorted = useAppSelector(selectActiveStagesSortedByOrder);
    const stagesSorted = useAppSelector(selectStagesSortedByOrder);
    const stagesWithCounts = useAppSelector(selectStagesWithCandidateCounts);
    const activeStagesWithCounts = useAppSelector(selectActiveStagesWithCandidateCounts);
    const loading = useAppSelector(selectStagesLoading);
    const error = useAppSelector(selectStagesError);
    const newCandidateCount = useAppSelector(selectNewCandidateCount);
    const candidateCounts = useAppSelector(selectCandidateCounts);
    const totalCandidateCount = useAppSelector(selectTotalCandidateCount);
    const stageStats = useAppSelector(selectStageStats);
    const isDataStale = useAppSelector(selectIsStagesDataStale);

    // Action creators
    const fetchAll = useCallback(() => {
        return dispatch(fetchAllStages());
    }, [dispatch]);

    const fetchActive = useCallback(() => {
        return dispatch(fetchActiveStages());
    }, [dispatch]);

    const fetchWithCounts = useCallback(() => {
        return dispatch(fetchStagesWithCounts());
    }, [dispatch]);

    const create = useCallback((stageData: StageCreateDto) => {
        return dispatch(createStage(stageData));
    }, [dispatch]);

    const update = useCallback((id: string, stageData: StageUpdateDto) => {
        return dispatch(updateStage({ id, stageData }));
    }, [dispatch]);

    const deleteById = useCallback((stageId: string) => {
        return dispatch(deleteStage(stageId));
    }, [dispatch]);

    const refreshCounts = useCallback(() => {
        return dispatch(refreshCandidateCounts());
    }, [dispatch]);

    const clearStageError = useCallback(() => {
        dispatch(clearError());
    }, [dispatch]);

    // Stage count management
    const updateStageCount = useCallback((stageId: string, count: number) => {
        dispatch(updateCandidateCount({ stageId, count }));
    }, [dispatch]);

    const incrementStageCount = useCallback((stageId: string) => {
        dispatch(incrementCandidateCount(stageId));
    }, [dispatch]);

    const decrementStageCount = useCallback((stageId: string) => {
        dispatch(decrementCandidateCount(stageId));
    }, [dispatch]);

    const moveCandidateBetweenStages = useCallback((fromStageId: string, toStageId: string) => {
        dispatch(handleCandidateStageChange({ fromStageId, toStageId }));
    }, [dispatch]);

    // Helper function to get a stage by ID
    const getStageById = useCallback((stageId: string) => {
        return stages.find(stage => stage.id === stageId);
    }, [stages]);

    // Helper function to get stage name by ID
    const getStageNameById = useCallback((stageId: string) => {
        const stage = getStageById(stageId);
        return stage?.name || 'Unknown Stage';
    }, [getStageById]);

    // Helper function to get candidate count for a stage
    const getCandidateCountByStageId = useCallback((stageId: string) => {
        return candidateCounts[stageId] || 0;
    }, [candidateCounts]);

    // Helper function to check if we should fetch data
    const shouldFetchData = useCallback(() => {
        return stages.length === 0 || isDataStale;
    }, [stages.length, isDataStale]);

    // Auto-fetch data if needed
    const ensureDataLoaded = useCallback((includeInactive = false) => {
        if (shouldFetchData()) {
            if (includeInactive) {
                return fetchAll();
            } else {
                return fetchWithCounts();
            }
        }
        return Promise.resolve();
    }, [shouldFetchData, fetchAll, fetchWithCounts]);

    return {
        // State
        stages,
        activeStages,
        activeStagesSorted,
        stagesSorted,
        stagesWithCounts,
        activeStagesWithCounts,
        loading,
        error,
        newCandidateCount,
        candidateCounts,
        totalCandidateCount,
        stageStats,
        isDataStale,

        // Actions
        fetchAll,
        fetchActive,
        fetchWithCounts,
        create,
        update,
        deleteById,
        refreshCounts,
        clearStageError,

        // Count management
        updateStageCount,
        incrementStageCount,
        decrementStageCount,
        moveCandidateBetweenStages,

        // Helpers
        getStageById,
        getStageNameById,
        getCandidateCountByStageId,
        shouldFetchData,
        ensureDataLoaded
    };
};

/**
 * Hook specifically for getting a single stage by ID
 * Returns the stage data and a loading state
 */
export const useStageById = (stageId: string | undefined) => {
    const stage = useAppSelector(state => stageId ? selectStageById(state, stageId) : null);
    const loading = useAppSelector(selectStagesLoading);
    const error = useAppSelector(selectStagesError);
    const { ensureDataLoaded } = useStages();

    // Auto-fetch if stage not found and not loading
    React.useEffect(() => {
        if (stageId && !stage && !loading) {
            ensureDataLoaded(true); // Include inactive stages
        }
    }, [stageId, stage, loading, ensureDataLoaded]);

    return {
        stage,
        loading,
        error,
        found: Boolean(stage)
    };
};

export default useStages;